import React, { useMemo, useState, useCallback } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import FolderTags from "../../components/BuilderComponents/FolderTags";
import Button from "../../components/FormComponents/Button";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import Input from "../../components/FormComponents/Input";
import LeftSideBar, { FolderCollapse } from "../../components/SideBar/LeftSideBar";
import { useAppBlocksContext } from "../../context/AppBlocksContext";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { useModalContext } from "../../context/ModalContext";
import { useNavigationContext } from "../../context/NavigationContext";
import { useScreenContext } from "../../context/ScreenContext";
import { useTableContext } from "../../context/TableContext";
import { useTreeContext } from "../../context/TreeDataContext";
import Loader from "../../Layouts/Loader/Loader";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Navbar from "../../Layouts/Navbar/Navbar";
import ResizableComponent from "../../Layouts/ResizableComponent";
import { filterExplorerData, organizeScreenFolders } from "../../utils/screenutils";
import { getPositionedElementConfig } from "../FlexibleLayout/utils";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import ScreenTagFilters from "./ScreenTagFilters";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import { ScreenGenerationWithAI } from "./ScreenRenderer";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import useQuery from "../../hooks/useQuery";

const ScreenSection = () => {
  const { screensExplorerData, setScreensExplorerData } = useTreeContext();
  const { selectedScreen, fetchAllScreens, setSelectedScreen } = useScreenContext();
  const { getAllTableData, getAllRdbmsData } = useTableContext();
  const { fetchAllApplicationBlock, setAllBlocksData } = useAppBlocksContext();

  const { screenId } = useParams();

  const multiFetch = useCallback(async ({ signal }) => {
    const [tables, rdbms, blocks, screens] = await Promise.all([
      getAllTableData({ signal }),
      getAllRdbmsData({ signal }),
      fetchAllApplicationBlock({ signal }),
      fetchAllScreens({ signal })
    ]);

    return { tables, rdbms, blocks, screens };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 2. useQu// eslint-disable-next-line react-hooks/exhaustive-depsery with stable key and abort support ----
  const { error, isLoading, refetch } = useQuery(null, multiFetch, {
    // optional, but makes consuming easier
    onSuccess: ({ blocks, screens }) => {
      if (!screensExplorerData?.length && screens?.treeData) {
        setScreensExplorerData(screens.treeData);
      }

      if (selectedScreen?.id && !screenId) {
        setSelectedScreen({ id: "" });
      }

      console.log("block", blocks, "screen", screens);
      if (blocks?.data) {
        setAllBlocksData(blocks.data);
      }
    },

    onError: (err) => {
      handleApiError(err);
    }
  });

  return (
    <section className="main_section">
      <div className="sub_section">
        {error ? <TryAgainMessage retryFunction={refetch} /> : <ScreenSectionRender isLoader={isLoading} />}
      </div>
    </section>
  );
};

const ScreenSectionRender = ({ isLoader }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAIScreenGen, setShowAIScreenGen] = useState(false);
  const [screenFilters, setScreenFilters] = useState({ tags: [] });

  const { screensExplorerData, setScreensExplorerData, selectedDevice } = useTreeContext();
  const { selectedScreen } = useScreenContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { saveChangesAndNavigate } = useNavigationContext();

  const navigate = useNavigate();

  const handleNewInsert = async (folderId, name) => {
    try {
      const trimName = name.trim();
      if (trimName.toLowerCase() === "header" || trimName.toLowerCase() === "home") {
        toastError("Name 'header' or 'home' is not allowed.");
        return;
      }

      const config = {
        name,
        device: folderId.name
      };

      const { data, message } = await axiosInstance.post(`/screens`, config);
      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folderId.name);

      const newTree = insertNewFileOrFolder(screensExplorerData, folderId, newItem);
      setScreensExplorerData(newTree);
      navigate(`./${data.id}`);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (folder) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(folder.name);
      if (!userConfirmed) return;

      const { message } = await axiosInstance.delete(`/screens/${folder.id}`);
      const newTree = deleteFolderOfFile(screensExplorerData, folder);
      setScreensExplorerData([...newTree]);

      if (folder.id === selectedScreen?.id) {
        navigate("./");
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = async (folder, newName) => {
    try {
      if (folder.isFolder) {
        const device = folder.id.split("-")[0];
        const deviceIndex = device === "desktop" ? 0 : 1;

        const folderExists = screensExplorerData[deviceIndex].items.some(
          (item) => item.isFolder && item.name.toLowerCase() === newName.toLowerCase() && item.id !== folder.id
        );

        if (folderExists) {
          toastError(`Folder "${newName}" already exists`);
          return;
        }

        const folderTagId = folder.id.split("-")[1];
        const config = { name: newName };

        const { message } = await axiosInstance.patch(`/folder-tag/${folderTagId}`, config);

        setScreensExplorerData((prevData) => {
          const newData = [...prevData];

          const findAndUpdateFolder = (items) =>
            items.map((item) => {
              if (item.id === folder.id) {
                return { ...item, name: newName };
              }
              if (item.items) {
                return { ...item, items: findAndUpdateFolder(item.items) };
              }
              return item;
            });

          newData[deviceIndex].items = findAndUpdateFolder(newData[deviceIndex].items);
          return newData;
        });

        toastSuccess(message);
      } else {
        const config = { name: newName };
        const { message } = await axiosInstance.patch(`/screens/${folder.id}`, config);
        const newTree = editFolderOrFile(screensExplorerData, folder, newName);
        setScreensExplorerData([...newTree]);
        toastSuccess(message);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedScreen?.id === id) return;
    saveChangesAndNavigate(id);
  };

  const reorderHeaderFirst = useCallback((items) => {
    if (!Array.isArray(items)) return items;

    return items
      .map((item) => {
        const newItem = { ...item };
        if (Array.isArray(newItem.items)) {
          newItem.items = reorderHeaderFirst(newItem.items);
        }
        return newItem;
      })
      .map((item) => {
        if (Array.isArray(item.items)) {
          const headers = item.items.filter((sub) => sub.name?.toLowerCase() === "header");
          const nonHeaders = item.items.filter((sub) => sub.name?.toLowerCase() !== "header");
          item.items = [...headers, ...nonHeaders];
        }
        return item;
      })
      .sort((a, b) => {
        const aIsHeader = a.name?.toLowerCase() === "header";
        const bIsHeader = b.name?.toLowerCase() === "header";
        if (aIsHeader && !bIsHeader) return -1;
        if (!aIsHeader && bIsHeader) return 1;
        return 0;
      });
  }, []);

  const filteredExplorerData = useMemo(() => {
    const filtered = filterExplorerData(screensExplorerData, screenFilters);
    return reorderHeaderFirst(filtered);
  }, [screensExplorerData, screenFilters, reorderHeaderFirst]);

  const createScreens = async (screensData) => {
    try {
      const screenConfig = {
        name: screensData.name,
        device: selectedDevice,
        is_offline: screensData.is_offline,
        is_positioned_screen: screensData.is_positioned_screen,
        folder_info_tag: screensData.folder_info_tag
      };

      if (screensData.is_positioned_screen) {
        setPositionedBlock(screenConfig, selectedDevice);
      }

      const { data: newScreen, message } = await axiosInstance.post(`/screens`, screenConfig);
      const { data: folderTagsData } = await axiosInstance.get("/folder-tag");

      const newScreenItem = {
        id: newScreen.id,
        name: newScreen.name,
        parentData: `${selectedDevice}/screen`,
        isFolder: false,
        can_delete: true,
        can_edit: true,
        fileInfo: {
          ...newScreen,
          folder_info_tag: screenConfig.folder_info_tag || []
        }
      };

      setScreensExplorerData((prevData) =>
        organizeScreenFolders(prevData, newScreenItem, selectedDevice, screenConfig.folder_info_tag, folderTagsData)
      );

      navigate(`./${newScreen.id}`);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleAIGenModal = () => {
    setShowAIScreenGen(true);
    setIsModalOpen(false);
  };

  return (
    <>
      <ResizableComponent
        resizeSide="right"
        storageKey="left_sidebar_size"
        defaultSize={244}
        range={[244, 400]}
        collapsible={true}
      >
        <HeaderModes />
        <div className="d_flex">
          <Navbar />
          <LeftSideBar
            explorerData={filteredExplorerData}
            setExplorerData={setScreensExplorerData}
            selectedScreen={selectedScreen}
            callbackFunction={callbackFunction}
            handleNewInsert={handleNewInsert}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            type="Screens"
            isLoader={isLoader}
            actionNeeded={true}
          >
            <MaterialIcon name="add" onClick={() => setIsModalOpen(true)} className="sidebar_icons" />

            <ScreenTagFilters checkedFilters={screenFilters} setCheckedFilters={setScreenFilters} />

            <FolderCollapse
              explorerData={filterExplorerData(screensExplorerData, screenFilters)}
              setExplorerData={setScreensExplorerData}
              type="Screens"
            />
          </LeftSideBar>
        </div>
      </ResizableComponent>

      {!isLoader ? <Outlet /> : <Loader />}

      {/* Modals now live here */}
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Create Screen">
        <CreateScreensModal
          createScreens={createScreens}
          setIsModalOpen={setIsModalOpen}
          handleAIScreen={handleAIGenModal}
        />
      </BuilderModal>

      <BuilderModal
        isOpen={showAIScreenGen}
        onClose={() => setShowAIScreenGen(false)}
        modalName="Generate Screens With AI"
      >
        <ScreenGenerationWithAI />
      </BuilderModal>
    </>
  );
};

const CreateScreensModal = ({ createScreens, setIsModalOpen, handleAIScreen }) => {
  const [screenData, setScreenData] = useState({
    name: "",
    is_offline: false,
    is_positioned_screen: true,
    folder_info_tag: []
  });
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setScreenData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleCreate = async () => {
    if (screenData.name.length < 3) {
      setIsError(true);
      return;
    }

    setIsLoading(true);
    await createScreens(screenData);
    setIsLoading(false);
    setIsModalOpen(false);
  };

  const handleFolderTagChange = (value, field) => {
    setScreenData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="builder_form">
      <Input
        type="checkbox"
        label="WYSIWYG Screen"
        name="is_positioned_screen"
        checked={screenData.is_positioned_screen}
        onChange={handleChange}
        containerClassName="d_flex justify_start"
      />

      <Input
        type="checkbox"
        label="Offline"
        name="is_offline"
        checked={screenData.is_offline}
        onChange={handleChange}
        containerClassName="d_flex justify_start"
      />

      <Input
        type="text"
        label="Screen Name"
        placeholder="Enter Screen Name"
        name="name"
        value={screenData.name}
        onChange={handleChange}
      />

      {isError && <ErrorMessage>Minimum 3 character required</ErrorMessage>}

      <FolderTags tagValue={screenData.folder_info_tag} onChange={handleFolderTagChange} />
      <div className="d_flex ml_auto w_auto gap_2">
        <Button onClick={handleAIScreen} isLoading={isLoading} variant="secondary">
          Design Screens with AI
        </Button>
        <Button onClick={handleCreate} isLoading={isLoading}>
          Create
        </Button>
      </div>
    </div>
  );
};

export const TryAgainMessage = ({ retryFunction, message = "oops something went wrong" }) => {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed"
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <h1
          style={{
            fontWeight: "bold",
            textAlign: "center",
            padding: "10px"
          }}
        >
          {message}
        </h1>
        <Button onClick={() => retryFunction()} variant="secondary">
          Try Again
        </Button>
      </div>
    </div>
  );
};
const setPositionedBlock = (config, device) => {
  config.is_positioned_screen = true; // NOTE: Remove this after the new screen is ready
  config.position_screen_config = [];
  const defaultSection = getPositionedElementConfig("section");
  config.position_screen_config.push(defaultSection);
  if (device === "mobile") {
    config.position_screen_metadata = {
      total_grid: 128,
      grid_size: 4
    };
  }
  if (device === "desktop") {
    config.screen_settings = {
      center: true
    };
  }
};

export default ScreenSection;
